import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 8 to 20

function addDays(d, n) {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function fmtDate(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=Sun
  x.setDate(x.getDate() - day);
  return x;
}

const STATUS_COLOR = {
  confirmed: "bg-primary/15 text-primary border-primary/30",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  in_progress: "bg-purple-100 text-purple-800 border-purple-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

export default function CalendarView() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [appts, setAppts] = useState([]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const fromDate = fmtDate(days[0]);
  const toDate = fmtDate(days[6]);

  useEffect(() => {
    api.get("/appointments", { params: { from_date: fromDate, to_date: toDate } }).then(r => setAppts(r.data));
  }, [fromDate, toDate]);

  const byCell = useMemo(() => {
    const map = {};
    appts.forEach(a => {
      const d = new Date(a.slot_start);
      const key = `${fmtDate(d)}_${d.getHours()}`;
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [appts]);

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Schedule</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Week calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <div className="text-sm text-muted-foreground ml-2 font-mono">{fromDate} → {toDate}</div>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border bg-muted/40">
              <div></div>
              {days.map((d, i) => {
                const isToday = fmtDate(d) === fmtDate(new Date());
                return (
                  <div key={i} className={`px-3 py-3 text-center border-l border-border ${isToday ? "bg-primary/5" : ""}`}>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.toLocaleDateString([], { weekday: "short" })}</div>
                    <div className={`font-heading text-xl font-bold mt-1 ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {/* Rows */}
            {HOURS.map(h => (
              <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border min-h-[64px]">
                <div className="px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-r border-border">{`${h.toString().padStart(2, "0")}:00`}</div>
                {days.map((d, di) => {
                  const k = `${fmtDate(d)}_${h}`;
                  const list = byCell[k] || [];
                  return (
                    <div key={di} className="border-l border-border p-1 space-y-1 align-top">
                      {list.map(a => (
                        <Link key={a.id} to="/appointments" className={`block rounded-md border px-2 py-1.5 text-[11px] ${STATUS_COLOR[a.status] || "bg-muted"}`} data-testid={`cal-evt-${a.id}`}>
                          <div className="font-mono text-[10px] opacity-80">{new Date(a.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                          <div className="font-semibold truncate">{a.pet?.name || "—"} · {a.service?.name?.slice(0, 18) || ""}</div>
                          <div className="truncate opacity-70">{a.customer?.name || ""}</div>
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
